/*
 * THE LEAD RECORD'S SMALL RULES (ContactDetailPage and its children).
 *
 * Pure, so they are tested without a browser (test/record-model.test.js). The
 * page decides layout; these decide what it says.
 */

/* ------------------------------------------------------------ call or thread */

// The stages whose panel is the call: In progress (PR #98) reads like
// Evaluating, so the two order the same way against the latest thread.
const CALL_STAGES = new Set(["in_progress", "evaluating"]);

/**
 * Does the call panel go above the Latest thread card? Whichever spoke most
 * recently leads, but only at a stage whose panel IS the call; elsewhere the
 * stage panel is about the pipeline position, which is not a dated event.
 */
export function callLeads({ call, thread, stage }) {
  if (!call || !thread || !CALL_STAGES.has(stage)) return false;
  return new Date(call.occurred_at) > new Date(thread.newest_at);
}

/* --------------------------------------------------------------- lead status */

/*
 * The lead_status values the CRM itself writes or defines (docs/SPEC.md):
 * ghosted (the ghost sweep), ignored (rule 2b), new_unreviewed (triage), and
 * the SPEC's own vocabulary. Anything else on the column is HubSpot's
 * hs_lead_status, copied in by the migration and frozen since ("Closed Won" on
 * client contacts, "IN_PROGRESS", "NEW", "CONNECTED"). It stays in the data;
 * the header just does not print it as a status the CRM is asserting.
 */
export const CRM_LEAD_STATUSES = new Set([
  "ghosted",
  "ignored",
  "new_unreviewed",
  "evaluating",
  "awaiting_decision",
  "not_a_fit",
  "advisory",
]);

/** The contact's lead_status if it is one of ours, else null. */
export function crmLeadStatus(value) {
  return typeof value === "string" && CRM_LEAD_STATUSES.has(value) ? value : null;
}

/* ------------------------------------------------------------ mark as vendor */

// Statuses markVendor (src/notALead.js) never demotes: a relationship somebody
// asserted is not a vendor because one person there sells to us.
const KEPT_STATUSES = new Set(["client", "churned", "vendor", "advisory"]);
const STATUS_WORDS = { client: "a client", churned: "churned", vendor: "a vendor", advisory: "advisory" };

/**
 * What "Mark as vendor" will change, as sentences for its confirm. Mirrors the
 * two paths CompanyCard takes: with a point of contact it goes through
 * POST /contacts/:id/vendor (markVendor), without one it sets the status
 * directly, and the two do different things to a client.
 *
 * `poc` is the company's point of contact, or null.
 */
export function vendorEffects({ company, poc = null }) {
  const name = (company && company.name) || "This company";
  const lines = [];
  const goes = `${name} becomes a vendor: off the board and out of every report, with its timeline kept.`;

  if (company && company.primary_contact_id) {
    const who = poc ? contactLabel(poc) : "The point of contact";
    if (KEPT_STATUSES.has(company.status)) {
      lines.push(`${name} stays ${STATUS_WORDS[company.status]}. Its status does not change.`);
    } else {
      lines.push(goes);
    }
    lines.push(
      `${who} becomes an associated contact: off the Leads list and out of the funnel, and no longer the point of contact at ${name}.`
    );
  } else {
    lines.push(
      company && company.status === "client" ? `${name} stops being a client. ${goes}` : goes
    );
    lines.push("No contact changes, because none is set as the point of contact.");
  }
  return lines;
}

function contactLabel(c) {
  const full = [c.first_name, c.last_name].filter(Boolean).join(" ").trim();
  return full || c.email || "The point of contact";
}

/* ---------------------------------------------------------------------- urls */

/**
 * Percent-decoded for reading, never for the link. LinkedIn slugs carry
 * encoded emoji and accents ("shahar-bahat-%e2%98%84..."), and a malformed
 * sequence (a stray "%", a truncated emoji) is kept as typed rather than
 * throwing: each run of escapes is decoded on its own.
 */
export function decodeForDisplay(s) {
  const str = String(s);
  try {
    return decodeURIComponent(str);
  } catch {
    return str.replace(/(?:%[0-9a-f]{2})+/gi, (run) => {
      try {
        return decodeURIComponent(run);
      } catch {
        return run;
      }
    });
  }
}

/**
 * The readable form of a URL: no scheme, no "www.", no trailing slash, and
 * decoded. The link still points at the full address. This is a dense row, and
 * "https://www.linkedin.com/in/noabar" spends half a column on boilerplate.
 */
export function prettyUrl(v) {
  if (v === null || v === undefined || (typeof v === "string" && !v.trim())) return v;
  const bare = String(v).trim().replace(/^https?:\/\//i, "").replace(/^www\./i, "").replace(/\/+$/, "");
  return decodeForDisplay(bare);
}
